import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, errMsg } from '../lib/api';
import { useDocumentTitle, useTenant } from '../lib/hooks';
import { addDaysIso, CLASSE_COR, classificar, fmtDayMon, fmtIsoFull, fmtTime, GRAVIDADE_LABEL, photoBg, plural, relDay } from '../lib/format';
import type { Gravidade, PhotoItem, SetorGrupo, VisitWork } from '../lib/types';
import { uploads, uploadsDurable, useUploads, type UploadItem } from '../lib/uploader';
import { Button, ErrorState, Field, GravidadePicker, GravPill, Loading, Segmented, Sheet, useConfirm } from '../components/ui';
import { AutoTextarea, SectorSelect } from '../components/fields';
import { photoToLightbox, useLightbox, useToast } from '../components/feedback';

/** Salva com atraso enquanto a pessoa digita; `flush` grava na hora (ao sair do campo). */
function useAutosave(save: (v: string) => Promise<unknown>, delay = 800) {
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef<string | null>(null);
  const run = async () => {
    const v = pending.current;
    if (v === null) return;
    pending.current = null;
    setState('saving');
    try { await save(v); setState('saved'); } catch { setState('error'); }
  };
  const change = (v: string) => {
    pending.current = v;
    window.clearTimeout(timer.current);
    setState('saving');
    timer.current = window.setTimeout(run, delay);
  };
  const flush = () => { window.clearTimeout(timer.current); return run(); };
  useEffect(() => () => { window.clearTimeout(timer.current); void run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { state, change, flush };
}

const saveLabel = (s: 'idle' | 'saving' | 'saved' | 'error') => (s === 'saving' ? 'salvando…' : s === 'saved' ? 'salvo' : s === 'error' ? 'não salvou · tente de novo' : '');

export function VisitPage() {
  const { tid, vid } = useParams() as { tid: string; vid: string };
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const lightbox = useLightbox();
  const confirm = useConfirm();
  const tenant = useTenant(tid);
  const key = ['visit', tid, vid];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<VisitWork>(`/api/t/${tid}/visits/${vid}`), refetchOnWindowFocus: false });
  const local = useUploads(vid);
  const d = q.data;
  const editing = d?.visit.status === 'finalizada';
  useDocumentTitle(d ? `${editing ? 'Editar relatório' : 'Visita'} · ${d.restaurant.nome}` : 'Visita');
  const refresh = () => qc.invalidateQueries({ queryKey: key });

  // Formulário de captura (setor fica; observação/NC/vínculo zeram após cada foto).
  const sectors = tenant.data?.sectors.map((s) => s.nome) ?? [];
  const [setor, setSetor] = useState('');
  const [link, setLink] = useState('');
  const [legenda, setLegenda] = useState('');
  const [isNc, setIsNc] = useState(false);
  const [gravidade, setGravidade] = useState<Gravidade>('moderada');
  const [askAfter, setAskAfter] = useState<{ id: string; titulo: string; setor: string } | null>(null);
  const [editNc, setEditNc] = useState<{ id: string; titulo: string; setor: string; gravidade: Gravidade } | null>(null);
  const [quick, setQuickState] = useState(() => { try { return localStorage.getItem('mesa.quickMode') === '1'; } catch { return false; } });
  const setQuick = (v: boolean) => { setQuickState(v); try { localStorage.setItem('mesa.quickMode', v ? '1' : '0'); } catch { /* sem storage */ } };
  const [editingPhoto, setEditingPhoto] = useState<{ id: string; queue: string[] | null } | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [addSetor, setAddSetor] = useState(false);
  const [grouping, setGrouping] = useState(false);
  useEffect(() => { if (!setor && sectors.length) setSetor(sectors.includes('Pré-preparo') ? 'Pré-preparo' : sectors[0]); }, [sectors, setor]);

  // Resumo (observações gerais) com salvamento automático.
  const [notes, setNotes] = useState<string | null>(null);
  useEffect(() => { if (d && notes === null) setNotes(d.visit.resumo ?? ''); }, [d, notes]);
  const notesSave = useAutosave((text) => api.patch(`/api/t/${tid}/visits/${vid}`, { resumo: text }));

  useEffect(
    () =>
      uploads.setHooks(vid, {
        onPresigned: (createdNc) => createdNc && void refresh(),
        onDone: () => void refresh(),
      }),
    [vid], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const mark = useMutation({
    mutationFn: ({ ncId, status }: { ncId: string; status: 'resolvida' | 'persiste' | null }) => api.put(`/api/t/${tid}/visits/${vid}/marks/${ncId}`, { status }),
    onMutate: async ({ ncId, status }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<VisitWork>(key);
      if (prev) qc.setQueryData<VisitWork>(key, { ...prev, pending: prev.pending.map((p) => (p.id === ncId ? { ...p, mark: status } : p)) });
      return { prev };
    },
    onError: (e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); toast(errMsg(e), 'err'); },
  });

  const removePhoto = useMutation({
    mutationFn: (id: string) => api.del(`/api/t/${tid}/visits/${vid}/photos/${id}`),
    onSuccess: () => refresh(),
    onError: (e) => toast(errMsg(e), 'err'),
  });

  const saveNc = useMutation({
    mutationFn: (n: { id: string; titulo: string; setor: string; gravidade: Gravidade }) => api.patch(`/api/t/${tid}/visits/${vid}/ncs/${n.id}`, { titulo: n.titulo, setor: n.setor, gravidade: n.gravidade }),
    onSuccess: () => { setEditNc(null); void refresh(); },
    onError: (e) => toast(errMsg(e), 'err'),
  });

  const changeDate = useMutation({
    mutationFn: (data: string) => api.patch(`/api/t/${tid}/visits/${vid}`, { data }),
    onSuccess: (_r, data) => {
      setDateOpen(false);
      void refresh();
      for (const k of ['home', 'restaurant', 'tracking', 'visits', 'period', 'report', 'agenda']) qc.invalidateQueries({ queryKey: [k, tid] });
      toast(`Data da visita: ${fmtIsoFull(data)}`);
    },
    onError: (e) => toast(errMsg(e), 'err'),
  });

  const finish = useMutation({
    mutationFn: async () => {
      await notesSave.flush();
      return api.post(`/api/t/${tid}/visits/${vid}/finalize`, { resumo: notes ?? '' });
    },
    onSuccess: () => {
      for (const k of ['home', 'restaurant', 'restaurants', 'tracking', 'photos', 'visits', 'period', 'agenda']) qc.invalidateQueries({ queryKey: [k, tid] });
      toast('Relatório gerado');
      nav(`/t/${tid}/relatorio/${vid}`, { replace: true });
    },
    onError: (e) => {
      toast(errMsg(e), 'err');
      if (e instanceof ApiError && (e.code === 'setor_sem_nota' || e.code === 'sem_setor')) document.getElementById('setores')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  });

  const doneEditing = async () => {
    await notesSave.flush();
    for (const k of ['home', 'restaurant', 'restaurants', 'tracking', 'photos', 'visits', 'period', 'report']) qc.invalidateQueries({ queryKey: [k, tid] });
    toast('Alterações salvas · o PDF é refeito em instantes');
    nav(`/t/${tid}/relatorio/${vid}`, { replace: true });
  };

  const linkOptions = useMemo(() => {
    if (!d) return { pending: [], created: [] };
    // Relatório fechado: só dá para vincular às pendências que já fazem parte dele.
    if (editing) return { pending: d.history.map((h) => ({ id: h.id, titulo: h.titulo, setor: h.setor })), created: d.created };
    return { pending: d.pending, created: d.created };
  }, [d, editing]);

  if (q.error instanceof ApiError && q.error.status === 404) return <Navigate to={`/t/${tid}`} replace />;
  if (d && !d.canWrite) return <Navigate to={editing ? `/t/${tid}/relatorio/${vid}` : `/t/${tid}`} replace />;

  const onFiles = (files: FileList | null) => {
    if (!files?.length || !d) return;
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name)) {
        toast('Escolha um arquivo de imagem.', 'err');
        continue;
      }
      uploads.add(tid, vid, file, quick
        ? { setor: '', legenda: '', isNc: false, ncId: null }
        : { setor, legenda: legenda.trim(), isNc: isNc && !link, ncId: link || null, gravidade })
        .catch((e) => toast(`Não foi possível guardar a foto: ${errMsg(e)}`, 'err'));
    }
    setLegenda('');
    setIsNc(false);
    setLink('');
  };

  const sending = local.filter((u) => u.status !== 'failed');
  const knownLocal = new Set(local.flatMap((u) => [u.localId, u.photoId].filter(Boolean) as string[]));
  // Envio interrompido sem cópia no aparelho (ex.: outro celular, dados apagados): a foto não existe mais.
  const interrupted = d?.photos.filter((p) => p.status === 'pending' && !knownLocal.has(p.id) && !(p.clientId && knownLocal.has(p.clientId))) ?? [];
  const failed = local.filter((u) => u.status === 'failed');
  const uploaded = d?.photos.filter((p) => p.status === 'uploaded') ?? [];
  const unclassified = uploaded.filter((p) => !p.setor);
  const semNota = d?.setores.filter((s) => s.nota === null) ?? [];
  const photoCount = uploaded.length + local.length;
  const titleFor = (ncId: string | null | undefined) => (ncId ? [...linkOptions.pending, ...linkOptions.created].find((n) => n.id === ncId)?.titulo : undefined);
  const linkedCount = (ncId: string) => d?.photos.filter((p) => p.ncId === ncId).length ?? 0;
  const linkedPending = d?.pending.find((p) => p.id === link);

  const blocker = sending.length
    ? `Enviando ${plural(sending.length, 'foto', 'fotos')}…`
    : failed.length || interrupted.length
      ? `${plural(failed.length + interrupted.length, 'foto não foi enviada', 'fotos não foram enviadas')}. Tente de novo ou descarte.`
      : unclassified.length
        ? `Falta escolher o setor de ${plural(unclassified.length, 'foto', 'fotos')}`
        : !d?.setores.length
          ? 'Dê a nota de pelo menos um setor'
          : semNota.length
            ? `Falta a nota de ${semNota.length === 1 ? semNota[0].setor : `${semNota.length} setores`}`
            : null;

  const cancel = async () => {
    const ok = await confirm.ask({
      title: 'Cancelar esta visita?',
      message: 'As fotos já enviadas e as não conformidades criadas nesta visita serão apagadas. Esta ação não pode ser desfeita.',
      cta: 'Cancelar visita',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/t/${tid}/visits/${vid}`);
      await uploads.clearVisit(vid);
      qc.invalidateQueries({ queryKey: ['home', tid] });
      qc.invalidateQueries({ queryKey: ['agenda', tid] });
      nav(d ? `/t/${tid}/restaurantes/${d.restaurant.id}` : `/t/${tid}`, { replace: true });
    } catch (e) {
      toast(errMsg(e), 'err');
    }
  };

  const capture = (label: string, sub: string, minHeight?: number) => (
    <label className="capture" style={minHeight ? { minHeight } : undefined}>
      <span className="t" style={{ fontSize: 16 }}>{label}</span>
      <span className="s">{sub}</span>
      <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
    </label>
  );
  const gallery = (label: string) => (
    <label className="link-btn u" style={{ display: 'inline-block', marginTop: 8, fontSize: 12.5 }}>
      {label}{' '}
      <input type="file" accept="image/jpeg,image/webp,image/heic,image/heif,image/png" multiple className="sr-only" onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
    </label>
  );

  return (
    <div style={{ minHeight: '100vh' }}>
      <header className="topbar">
        <div className="topbar-in" style={{ maxWidth: 1080, padding: '10px 16px', gap: 10 }}>
          <Button size="xs" onClick={() => (editing ? void doneEditing() : nav(-1))} aria-label="Voltar">←</Button>
          <div className="flex1" style={{ minWidth: 0 }}>
            <div className="ellipsis" style={{ fontSize: 14, fontWeight: 600 }}>{d?.restaurant.nome ?? ' '}</div>
            <button className="date-chip" onClick={() => d && setDateOpen(true)} disabled={!d} aria-label="Mudar a data da visita">
              <span className="dot" style={{ background: editing ? 'var(--persist)' : 'var(--accent)' }} />
              {editing ? `Editando relatório nº ${d?.visit.numero ?? ''}` : 'Visita em andamento'}
              {d ? ` · ${relDay(d.visit.data, d.visit.hoje)}` : ''} <span style={{ opacity: 0.7 }}>▾</span>
            </button>
          </div>
          {!editing && <Button size="xs" onClick={cancel} disabled={!d}>Cancelar</Button>}
        </div>
      </header>

      {q.error && <div className="page narrow"><ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} /></div>}
      {!d && !q.error && <div className="visit-grid"><Loading rows={3} height={140} /></div>}

      {d && (
        <div className="visit-grid v2 anim-fade">
          {editing && (
            <div className="card edit-banner">
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>Você está editando um relatório já finalizado</div>
              <div className="small" style={{ marginTop: 3 }}>
                Tudo é salvo na hora: notas, observações, fotos e a data. O PDF e a planilha são refeitos com as mudanças.
              </div>
            </div>
          )}

          {/* Pendências da última visita (em andamento) ou o que aconteceu com elas (relatório fechado) */}
          {!editing ? (
            <section className="card">
              {d.pending.length ? (
                <>
                  <h2 className="h2" style={{ marginBottom: 3 }}>Pendências da última visita</h2>
                  <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 10 }}>
                    Marque o que foi corrigido. Fotografe a correção vinculando à pendência para gerar o antes e depois.
                  </div>
                  {d.pending.map((p) => (
                    <div key={p.id} style={{ padding: '11px 0', borderTop: '1px solid var(--divider-2)' }}>
                      <div className="wrapline" style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.35 }}>{p.titulo}</div>
                      <div className="small wrapline" style={{ marginTop: 2 }}>
                        {p.setor} · {GRAVIDADE_LABEL[p.gravidade]} · aberta em {fmtDayMon(p.openedAt)}{linkedCount(p.id) ? ` · ${plural(linkedCount(p.id), 'foto vinculada', 'fotos vinculadas')}` : ''}
                      </div>
                      {p.reply && <div className="reply">Cliente: {p.reply.texto}</div>}
                      {p.mark === 'resolvida' && linkedCount(p.id) === 0 && (
                        <button className="link-btn u" style={{ marginTop: 8, fontSize: 12.5 }} onClick={() => setAskAfter(p)}>📷 Tirar foto da correção (depois)</button>
                      )}
                      <div className="row gap-6" style={{ marginTop: 9 }}>
                        <button
                          className={`mark res ${p.mark === 'resolvida' ? 'on' : ''}`}
                          aria-pressed={p.mark === 'resolvida'}
                          onClick={() => {
                            const resolving = p.mark !== 'resolvida';
                            mark.mutate({ ncId: p.id, status: resolving ? 'resolvida' : null });
                            if (resolving && linkedCount(p.id) === 0) setAskAfter(p);
                          }}
                        >
                          Resolvida
                        </button>
                        <button className={`mark per ${p.mark === 'persiste' ? 'on' : ''}`} aria-pressed={p.mark === 'persiste'} onClick={() => mark.mutate({ ncId: p.id, status: p.mark === 'persiste' ? null : 'persiste' })}>Persiste</button>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <h2 className="h2" style={{ marginBottom: 3 }}>{d.lastVisitAt ? 'Nenhuma pendência em aberto' : 'Primeira visita nesta unidade'}</h2>
                  <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                    {d.lastVisitAt ? `Última visita em ${fmtDayMon(d.lastVisitAt)} sem pendências abertas.` : 'As não conformidades registradas hoje passam a ser acompanhadas a partir desta visita.'}
                  </div>
                </>
              )}
            </section>
          ) : d.history.length > 0 ? (
            <section className="card">
              <h2 className="h2" style={{ marginBottom: 3 }}>Pendências anteriores nesta visita</h2>
              <div className="small" style={{ marginBottom: 8 }}>Já fazem parte do histórico e não mudam mais.</div>
              {d.history.map((h) => (
                <div key={h.id} className="row gap-10" style={{ padding: '9px 0', borderTop: '1px solid var(--divider-2)' }}>
                  <span className={`pill ${h.tipo === 'resolvida' ? 'res' : 'per'}`}>{h.tipo === 'resolvida' ? 'Resolvida' : 'Persiste'}</span>
                  <div className="flex1 wrapline" style={{ fontSize: 13 }}>{h.titulo} <span className="small">· {h.setor}</span></div>
                </div>
              ))}
            </section>
          ) : null}

          {/* Não conformidades abertas nesta visita */}
          {d.created.length > 0 && (
            <section className="card">
              <div className="section-head" style={{ marginBottom: 6 }}>
                <h2 className="h2">Novas não conformidades</h2>
                <span className="counter-mono">{d.created.length}</span>
              </div>
              {d.created.map((n) => (
                <div key={n.id} className="row gap-10" style={{ padding: '10px 0', borderTop: '1px solid var(--divider-2)', alignItems: 'flex-start' }}>
                  <div className="dot" style={{ background: 'var(--nc)', marginTop: 6 }} />
                  <div className="flex1">
                    <div className="wrapline" style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.35 }}>{n.titulo}</div>
                    <div className="small" style={{ marginTop: 2 }}>{n.setor}</div>
                  </div>
                  <GravPill g={n.gravidade} />
                  <button className="link-btn" onClick={() => setEditNc(n)}>Editar</button>
                </div>
              ))}
            </section>
          )}

          {/* Captura */}
          <section className="card">
            <div className="section-head" style={{ marginBottom: 12, alignItems: 'center' }}>
              <h2 className="h2">Tirar fotos <span className="counter-mono" style={{ fontWeight: 400 }}>· {plural(photoCount, 'foto', 'fotos')}</span></h2>
              <div style={{ width: 190 }}>
                <Segmented<'full' | 'quick'> fill label="Modo de captura" value={quick ? 'quick' : 'full'} onChange={(m) => setQuick(m === 'quick')} options={[['full', 'Completo'], ['quick', 'Rápido']]} />
              </div>
            </div>

            {quick ? (
              <>
                {capture('Tirar foto', 'Modo rápido: só fotografe. Depois você agrupa as fotos por setor e escreve as observações.', 132)}
                {gallery('Escolher várias da galeria')}
              </>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 10, marginBottom: 10 }}>
                  <label className="stack gap-6" style={{ cursor: 'default' }}>
                    <span className="label">Setor</span>
                    <SectorSelect tid={tid} sectors={sectors} value={setor} onChange={setSetor} />
                  </label>
                  <label className="stack gap-6" style={{ cursor: 'default' }}>
                    <span className="label">Vincular a pendência</span>
                    <select
                      className="select sm"
                      value={link}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLink(v);
                        const n = [...linkOptions.pending, ...linkOptions.created].find((x) => x.id === v);
                        if (n) { setSetor(n.setor); setIsNc(false); }
                      }}
                    >
                      <option value="">Nenhuma</option>
                      {linkOptions.pending.length > 0 && (
                        <optgroup label={editing ? 'Pendências anteriores' : 'Pendências anteriores'}>
                          {linkOptions.pending.map((n) => <option key={n.id} value={n.id}>{n.titulo}</option>)}
                        </optgroup>
                      )}
                      {linkOptions.created.length > 0 && (
                        <optgroup label="Abertas nesta visita">
                          {linkOptions.created.map((n) => <option key={n.id} value={n.id}>{n.titulo}</option>)}
                        </optgroup>
                      )}
                    </select>
                  </label>
                </div>
                {linkedPending && (
                  <div className="small" style={{ margin: '-2px 0 10px', color: linkedPending.mark === 'resolvida' ? 'var(--resolved)' : 'var(--text-2)' }}>
                    {linkedPending.mark === 'resolvida' ? 'Marcada como resolvida: esta foto entra como DEPOIS no relatório.' : 'Marque a pendência como resolvida para a foto entrar como DEPOIS no relatório.'}
                  </div>
                )}
                <AutoTextarea
                  style={{ marginBottom: 10 }}
                  placeholder={isNc ? 'Descreva a não conformidade (vira o título)' : 'Observação desta foto (opcional, sem limite de texto)'}
                  value={legenda}
                  onChange={(e) => setLegenda(e.target.value)}
                />
                <button type="button" className={`check-row ${isNc ? 'on' : ''}`} style={{ marginBottom: isNc ? 8 : 12 }} aria-pressed={isNc} onClick={() => { setIsNc(!isNc); setLink(''); }}>
                  <span className="box">{isNc ? '✓' : ''}</span>
                  <span className="flex1" style={{ fontSize: 13.5, fontWeight: 500 }}>Nova não conformidade</span>
                </button>
                {isNc && (
                  <div style={{ marginBottom: 12 }}>
                    <div className="small" style={{ marginBottom: 6 }}>Gravidade</div>
                    <GravidadePicker value={gravidade} onChange={setGravidade} />
                  </div>
                )}
                {capture('Tirar foto', `Entra no setor ${setor || '—'} · começa a enviar na hora`)}
                {gallery('Escolher da galeria')}
              </>
            )}

            {(local.length > 0 || interrupted.length > 0) && (
              <div className="stack gap-8" style={{ marginTop: 12 }}>
                <div className="small" style={{ color: 'var(--text-2)' }}>
                  {uploadsDurable()
                    ? 'As fotos ficam guardadas no aparelho até chegar ao servidor. Pode sair ou fechar o app: o envio continua.'
                    : 'Mantenha esta tela aberta até terminar o envio.'}
                </div>
                {[...local].reverse().map((u) => <LocalUpload key={u.localId} u={u} ncTitle={titleFor(u.ncId)} />)}
                {interrupted.map((p) => (
                  <div key={p.id} className="upl" style={{ borderColor: 'var(--nc-border)', background: '#FFFBFA' }}>
                    <div className="thumb" style={{ background: 'var(--divider)' }} />
                    <div className="flex1">
                      <div className="wrapline" style={{ fontSize: 13, fontWeight: 500 }}>{p.legenda || 'Sem observação'}</div>
                      <div className="small" style={{ color: 'var(--nc-text)', marginTop: 2 }}>Envio interrompido em outro aparelho · tire a foto de novo</div>
                      <button className="link-btn u muted" style={{ marginTop: 6 }} onClick={() => removePhoto.mutate(p.id)}>Descartar</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Setores: nota + observação + fotos agrupadas */}
          <section className="card" id="setores">
            <div className="section-head" style={{ marginBottom: 4 }}>
              <h2 className="h2">Avaliação por setor</h2>
              <NotaVisita nota={d.nota} setores={d.setores} />
            </div>
            <div className="small" style={{ marginBottom: 12 }}>
              Dê uma nota de 0 a 100 para cada setor. A nota da visita é a média. Escreva a observação do setor e, se quiser, uma observação em cada foto.
            </div>

            {unclassified.length > 0 && (
              <div className="setor-box" style={{ borderColor: 'var(--persist-border)', background: 'var(--persist-soft)' }}>
                <div className="row gap-8 wrap" style={{ marginBottom: 10 }}>
                  <div className="flex1" style={{ minWidth: 180 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--persist-text)' }}>{plural(unclassified.length, 'foto sem setor', 'fotos sem setor')}</div>
                    <div className="small">Escolha as fotos e agrupe no setor certo.</div>
                  </div>
                  <Button size="sm" variant="dark" onClick={() => setGrouping(true)}>Agrupar no setor</Button>
                </div>
                <PhotoStrip photos={unclassified} onOpen={(p) => setEditingPhoto({ id: p.id, queue: unclassified.map((x) => x.id) })} />
              </div>
            )}

            {d.setores.length === 0 && !unclassified.length && (
              <div className="empty" style={{ marginBottom: 10 }}>Tire fotos ou adicione um setor para dar a nota.</div>
            )}

            <div className="stack gap-10">
              {d.setores.map((s) => (
                <SetorCard
                  key={s.setor}
                  tid={tid}
                  vid={vid}
                  grupo={s}
                  photos={uploaded.filter((p) => p.setor === s.setor)}
                  onOpenPhoto={(p, list) => setEditingPhoto({ id: p.id, queue: list.map((x) => x.id) })}
                  onChanged={refresh}
                />
              ))}
            </div>
            <Button block style={{ marginTop: 12 }} onClick={() => setAddSetor(true)}>+ Avaliar outro setor (sem foto)</Button>
          </section>

          {/* Observações gerais */}
          <section className="card">
            <div className="section-head" style={{ marginBottom: 8 }}>
              <h2 className="h2">Resumo da visita</h2>
              <span className="counter-mono">{saveLabel(notesSave.state)}</span>
            </div>
            <AutoTextarea
              minRows={5}
              placeholder="Observações gerais que vão no topo do relatório (sem limite de texto)"
              value={notes ?? ''}
              onChange={(e) => { setNotes(e.target.value); notesSave.change(e.target.value); }}
              onBlur={() => void notesSave.flush()}
            />
          </section>
        </div>
      )}

      <div className="footer-fixed">
        <div className="in">
          <div className="row wrap gap-8" style={{ justifyContent: 'center' }}>
            {d && <NotaVisita nota={d.nota} setores={d.setores} long />}
            {blocker && <span style={{ fontSize: 12, color: failed.length ? 'var(--nc-text)' : 'var(--persist-text)', textAlign: 'center' }}>{blocker}</span>}
          </div>
          {editing ? (
            <Button variant="dark" size="lg" block disabled={!d} onClick={() => void doneEditing()}>
              {sending.length ? 'Concluir (as fotos terminam de subir sozinhas)' : 'Concluir edição e ver relatório'}
            </Button>
          ) : unclassified.length > 0 && !sending.length && !failed.length ? (
            <Button variant="dark" size="lg" block onClick={() => setGrouping(true)}>Agrupar {plural(unclassified.length, 'foto', 'fotos')} no setor</Button>
          ) : (
            <Button
              variant="dark" size="lg" block loading={finish.isPending} disabled={!d || sending.length > 0 || failed.length > 0}
              onClick={() => {
                if (blocker && semNota.length) { toast(blocker, 'err'); document.getElementById('setores')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
                finish.mutate();
              }}
            >
              {sending.length ? 'Aguardando envio das fotos' : 'Finalizar e gerar relatório'}
            </Button>
          )}
        </div>
      </div>

      {d && editingPhoto && (
        <PhotoEditor
          key={editingPhoto.id}
          tid={tid}
          vid={vid}
          work={d}
          editing={editing}
          sectors={sectors}
          photoId={editingPhoto.id}
          queue={editingPhoto.queue}
          onNext={(id) => setEditingPhoto(id ? { id, queue: editingPhoto.queue } : null)}
          onClose={() => setEditingPhoto(null)}
          onOpenFull={(p) => lightbox(photoToLightbox(p))}
          onDelete={async (p) => {
            if (await confirm.ask({ title: 'Remover foto?', message: p.tag === 'antes' && !editing ? 'A não conformidade criada com esta foto também será removida.' : 'A foto será apagada desta visita.', cta: 'Remover', danger: true })) {
              removePhoto.mutate(p.id);
              setEditingPhoto(null);
            }
          }}
        />
      )}

      {d && (
        <DateSheet
          open={dateOpen}
          onClose={() => setDateOpen(false)}
          value={d.visit.data}
          hoje={d.visit.hoje}
          editing={editing}
          saving={changeDate.isPending}
          onSave={(v) => changeDate.mutate(v)}
        />
      )}

      {d && (
        <AddSetorSheet
          open={addSetor}
          onClose={() => setAddSetor(false)}
          tid={tid}
          sectors={sectors.filter((s) => !d.setores.some((g) => g.setor === s))}
          onAdd={async (s) => {
            try {
              await api.put(`/api/t/${tid}/visits/${vid}/setores`, { setor: s });
              await refresh();
              setAddSetor(false);
              setTimeout(() => document.getElementById(`setor-${s}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
            } catch (e) {
              toast(errMsg(e), 'err');
            }
          }}
        />
      )}

      {d && grouping && (
        <GroupSheet
          tid={tid}
          vid={vid}
          sectors={sectors}
          photos={unclassified}
          onClose={() => setGrouping(false)}
          onDone={() => { setGrouping(false); void refresh(); }}
        />
      )}

      {/* Pendência resolvida → foto do "depois" (opcional) */}
      <Sheet open={!!askAfter} onClose={() => setAskAfter(null)} title="Foto da correção" subtitle={askAfter ? `${askAfter.titulo} · ${askAfter.setor}` : undefined}>
        <div className="sub" style={{ marginBottom: 14 }}>Fotografe como ficou depois de corrigido. No relatório, ela aparece ao lado da foto de antes.</div>
        <label className="capture" style={{ minHeight: 110 }}>
          <span className="t" style={{ fontSize: 16 }}>📷 Tirar foto do depois</span>
          <span className="s">Câmera abre direto</span>
          <input
            type="file" accept="image/*" capture="environment" className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f || !askAfter) return;
              uploads.add(tid, vid, f, { setor: askAfter.setor, legenda: `Corrigido: ${askAfter.titulo}`, isNc: false, ncId: askAfter.id })
                .then(() => toast('Foto do depois guardada · enviando'))
                .catch((err) => toast(errMsg(err), 'err'));
              setAskAfter(null);
            }}
          />
        </label>
        <Button block size="lg" style={{ marginTop: 10 }} onClick={() => setAskAfter(null)}>Pular</Button>
      </Sheet>

      <Sheet open={!!editNc} onClose={() => setEditNc(null)} title="Não conformidade">
        {editNc && (
          <form onSubmit={(e) => { e.preventDefault(); saveNc.mutate(editNc); }}>
            <Field label="Descrição"><AutoTextarea value={editNc.titulo} onChange={(e) => setEditNc({ ...editNc, titulo: e.target.value })} /></Field>
            <Field label="Setor">
              <SectorSelect tid={tid} className="select" sectors={sectors} value={editNc.setor} onChange={(v) => setEditNc({ ...editNc, setor: v })} />
            </Field>
            <div className="field">
              <span className="label">Gravidade</span>
              <GravidadePicker value={editNc.gravidade} onChange={(g) => setEditNc({ ...editNc, gravidade: g })} />
            </div>
            <Button variant="dark" size="lg" block type="submit" loading={saveNc.isPending}>Salvar</Button>
          </form>
        )}
      </Sheet>
      {confirm.node}
    </div>
  );
}

/** Nota da visita (média dos setores) com a cor da classificação. */
function NotaVisita({ nota, setores, long }: { nota: number | null; setores: SetorGrupo[]; long?: boolean }) {
  const comNota = setores.filter((s) => s.nota !== null).length;
  if (nota === null) return <span className="pill" style={{ background: 'var(--divider-2)', color: 'var(--text-2)' }}>{long ? 'Nota da visita: dê nota aos setores' : 'sem nota'}</span>;
  const c = classificar(nota);
  const cor = CLASSE_COR[c];
  return (
    <span className="pill" style={{ background: cor.bg, color: cor.fg, fontWeight: 600, border: `1px solid ${cor.fg}33` }}>
      {long ? `Nota da visita ${nota} · ${c} · média de ${plural(comNota, 'setor', 'setores')}` : `Média ${nota} · ${c}`}
    </span>
  );
}

/** Um setor da visita: nota, observação do setor e as fotos dele (cada uma com sua observação). */
function SetorCard({ tid, vid, grupo, photos, onOpenPhoto, onChanged }: {
  tid: string; vid: string; grupo: SetorGrupo; photos: PhotoItem[];
  onOpenPhoto: (p: PhotoItem, list: PhotoItem[]) => void; onChanged: () => void;
}) {
  const toast = useToast();
  const [nota, setNota] = useState(grupo.nota === null ? '' : String(grupo.nota));
  const [obs, setObs] = useState(grupo.observacao ?? '');
  const [obsOpen, setObsOpen] = useState(!!grupo.observacao);
  useEffect(() => { setNota(grupo.nota === null ? '' : String(grupo.nota)); }, [grupo.nota]);
  const put = (body: object) => api.put(`/api/t/${tid}/visits/${vid}/setores`, { setor: grupo.setor, ...body });
  const obsSave = useAutosave((v) => put({ observacao: v }));
  const notaSave = useAutosave(async (v) => {
    const n = v === '' ? null : Math.max(0, Math.min(100, Math.round(Number(v))));
    if (n !== null && Number.isNaN(n)) return;
    await put({ nota: n });
    onChanged();
  }, 700);
  const n = nota === '' ? null : Number(nota);
  const cor = n !== null && !Number.isNaN(n) ? CLASSE_COR[classificar(n)] : null;

  const setNum = (v: string) => {
    const clean = v.replace(/[^\d]/g, '').slice(0, 3);
    const val = clean === '' ? '' : String(Math.min(100, Number(clean)));
    setNota(val);
    notaSave.change(val);
  };

  return (
    <div className="setor-box" id={`setor-${grupo.setor}`} style={grupo.nota === null ? { borderColor: 'var(--persist-border)' } : undefined}>
      <div className="row gap-10" style={{ alignItems: 'center' }}>
        <div className="flex1" style={{ minWidth: 0 }}>
          <div className="wrapline" style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{grupo.setor}</div>
          <div className="small">{plural(photos.length, 'foto', 'fotos')}{grupo.nota === null ? ' · falta a nota' : ''}</div>
        </div>
        <label className="nota-input" style={cor ? { background: cor.bg, color: cor.fg, borderColor: `${cor.fg}55` } : undefined}>
          <span className="sr-only">Nota do setor {grupo.setor}</span>
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="—"
            value={nota}
            onChange={(e) => setNum(e.target.value)}
            onBlur={() => void notaSave.flush()}
            aria-label={`Nota de 0 a 100 para ${grupo.setor}`}
          />
          <span className="of">/100</span>
        </label>
      </div>
      <div className="row gap-6 wrap" style={{ marginTop: 8 }}>
        {[100, 90, 80, 70, 60, 50].map((v) => (
          <button key={v} type="button" className={`chip toggle sm ${nota === String(v) ? 'on' : ''}`} onClick={() => { setNota(String(v)); notaSave.change(String(v)); void notaSave.flush(); }}>{v}</button>
        ))}
        {cor && n !== null && <span className="small" style={{ alignSelf: 'center', color: cor.fg, fontWeight: 600 }}>{classificar(n)}</span>}
        <span className="counter-mono" style={{ marginLeft: 'auto', alignSelf: 'center' }}>{saveLabel(notaSave.state === 'idle' ? obsSave.state : notaSave.state)}</span>
      </div>

      {obsOpen ? (
        <AutoTextarea
          style={{ marginTop: 10 }}
          placeholder={`Observação geral do setor ${grupo.setor} (sem limite de texto)`}
          value={obs}
          onChange={(e) => { setObs(e.target.value); obsSave.change(e.target.value); }}
          onBlur={() => void obsSave.flush()}
        />
      ) : (
        <button className="link-btn u" style={{ display: 'block', marginTop: 10, fontSize: 13 }} onClick={() => setObsOpen(true)}>+ Observação do setor</button>
      )}

      {photos.length > 0 && <PhotoStrip photos={photos} onOpen={(p) => onOpenPhoto(p, photos)} />}

      {photos.length === 0 && (
        <button
          className="link-btn muted"
          style={{ display: 'block', marginTop: 10, fontSize: 12 }}
          onClick={async () => {
            try { await api.del(`/api/t/${tid}/visits/${vid}/setores?setor=${encodeURIComponent(grupo.setor)}`); onChanged(); } catch (e) { toast(errMsg(e), 'err'); }
          }}
        >
          Tirar este setor da visita
        </button>
      )}
    </div>
  );
}

/** Fotos do setor: miniatura + observação da foto (toque para escrever/editar). */
function PhotoStrip({ photos, onOpen }: { photos: PhotoItem[]; onOpen: (p: PhotoItem) => void }) {
  return (
    <div className="photo-list">
      {photos.map((p) => (
        <button key={p.id} type="button" className="photo-row" onClick={() => onOpen(p)}>
          <span className="thumb" style={{ background: photoBg(p.thumbUrl, p.id) }} />
          <span className="flex1" style={{ minWidth: 0 }}>
            <span className={`obs ${p.legenda ? "" : "vazia"}`}>{p.legenda || 'Toque para escrever a observação da foto'}</span>
            <span className="meta">
              {fmtTime(p.capturedAt)}
              {p.isNc && <span className="pill new" style={{ marginLeft: 6 }}>NC</span>}
              {p.tag === 'depois' && <span className="pill res" style={{ marginLeft: 6 }}>depois</span>}
            </span>
          </span>
          <span className="none" style={{ color: 'var(--accent)', fontSize: 12.5 }}>editar</span>
        </button>
      ))}
    </div>
  );
}

function DateSheet({ open, onClose, value, hoje, editing, saving, onSave }: {
  open: boolean; onClose: () => void; value: string; hoje: string; editing: boolean; saving: boolean; onSave: (v: string) => void;
}) {
  const [v, setV] = useState(value);
  useEffect(() => { if (open) setV(value); }, [open, value]);
  const ontem = addDaysIso(hoje, -1);
  return (
    <Sheet open={open} onClose={onClose} title="Dia da visita" subtitle={editing ? 'Mude se o relatório foi registrado com a data errada.' : 'O padrão é hoje. Mude se está registrando uma visita feita em outro dia.'}>
      <div className="row gap-8" style={{ marginBottom: 12 }}>
        <button type="button" className={`chip toggle ${v === hoje ? 'on' : ''}`} onClick={() => setV(hoje)}>Hoje</button>
        <button type="button" className={`chip toggle ${v === ontem ? 'on' : ''}`} onClick={() => setV(ontem)}>Ontem</button>
        <button type="button" className={`chip toggle ${v === addDaysIso(hoje, -2) ? 'on' : ''}`} onClick={() => setV(addDaysIso(hoje, -2))}>Anteontem</button>
      </div>
      <Field label="Outra data">
        <input className="input" type="date" max={hoje} value={v} onChange={(e) => e.target.value && setV(e.target.value)} />
      </Field>
      <div className="small" style={{ marginBottom: 14 }}>A ordem das visitas do restaurante segue a data.</div>
      <Button variant="dark" size="lg" block loading={saving} disabled={v === value || v > hoje} onClick={() => onSave(v)}>
        Salvar · {relDay(v, hoje)}
      </Button>
    </Sheet>
  );
}

function AddSetorSheet({ open, onClose, tid, sectors, onAdd }: { open: boolean; onClose: () => void; tid: string; sectors: string[]; onAdd: (s: string) => void }) {
  const [s, setS] = useState('');
  useEffect(() => { if (open) setS(''); }, [open]);
  return (
    <Sheet open={open} onClose={onClose} title="Avaliar outro setor" subtitle="Para dar nota a um setor mesmo sem tirar foto.">
      <div className="row gap-6 wrap" style={{ marginBottom: 12 }}>
        {sectors.map((x) => <button key={x} type="button" className={`chip toggle ${s === x ? 'on' : ''}`} onClick={() => setS(x)}>{x}</button>)}
      </div>
      <Field label="Ou escolha / crie um setor">
        <SectorSelect tid={tid} className="select" sectors={sectors} value={s} onChange={setS} allowEmpty />
      </Field>
      <Button variant="dark" size="lg" block disabled={!s} onClick={() => onAdd(s)}>Adicionar {s || 'setor'}</Button>
    </Sheet>
  );
}

/** Agrupar: escolhe várias fotos sem setor e coloca todas no mesmo setor. */
function GroupSheet({ tid, vid, sectors, photos, onClose, onDone }: { tid: string; vid: string; sectors: string[]; photos: PhotoItem[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [sel, setSel] = useState<Set<string>>(() => new Set(photos.map((p) => p.id)));
  const [setor, setSetor] = useState('');
  const save = useMutation({
    mutationFn: () => api.post(`/api/t/${tid}/visits/${vid}/photos/agrupar`, { photoIds: [...sel], setor }),
    onSuccess: () => { toast(`${plural(sel.size, 'foto agrupada', 'fotos agrupadas')} em ${setor}`); onDone(); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <Sheet open onClose={onClose} title="Agrupar fotos no setor" subtitle="Marque as fotos do mesmo setor e escolha qual é." wide>
      <div className="row between" style={{ marginBottom: 8 }}>
        <span className="small">{plural(sel.size, 'foto marcada', 'fotos marcadas')}</span>
        <button className="link-btn" onClick={() => setSel(sel.size === photos.length ? new Set() : new Set(photos.map((p) => p.id)))}>{sel.size === photos.length ? 'Desmarcar todas' : 'Marcar todas'}</button>
      </div>
      <div className="pick-grid">
        {photos.map((p) => (
          <button key={p.id} type="button" className={`pick ${sel.has(p.id) ? 'on' : ''}`} style={{ background: photoBg(p.thumbUrl, p.id) }} onClick={() => toggle(p.id)} aria-pressed={sel.has(p.id)}>
            <span className="tick">{sel.has(p.id) ? '✓' : ''}</span>
          </button>
        ))}
      </div>
      <div className="row gap-6 wrap" style={{ margin: '14px 0 10px' }}>
        {sectors.map((x) => <button key={x} type="button" className={`chip toggle ${setor === x ? 'on' : ''}`} onClick={() => setSetor(x)}>{x}</button>)}
      </div>
      <Field label="Setor">
        <SectorSelect tid={tid} className="select" sectors={sectors} value={setor} onChange={setSetor} allowEmpty />
      </Field>
      <Button variant="dark" size="lg" block loading={save.isPending} disabled={!setor || !sel.size} onClick={() => save.mutate()}>
        {setor ? `Colocar ${plural(sel.size, 'foto', 'fotos')} em ${setor}` : 'Escolha o setor'}
      </Button>
    </Sheet>
  );
}

/** Editar uma foto: observação (sem limite), setor, pendência e NC. "Salvar e próxima" percorre o grupo. */
function PhotoEditor({ tid, vid, work, editing, sectors, photoId, queue, onNext, onClose, onOpenFull, onDelete }: {
  tid: string; vid: string; work: VisitWork; editing: boolean; sectors: string[]; photoId: string; queue: string[] | null;
  onNext: (id: string | null) => void; onClose: () => void; onOpenFull: (p: PhotoItem) => void; onDelete: (p: PhotoItem) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const p = work.photos.find((x) => x.id === photoId);
  const ownNc = p?.tag === 'antes' && p.ncId ? p.ncId : null;
  const [setor, setSetor] = useState(p?.setor ?? '');
  const [legenda, setLegenda] = useState(p?.legenda ?? '');
  const [isNc, setIsNc] = useState(!!ownNc);
  const [grav, setGrav] = useState<Gravidade>(work.created.find((n) => n.id === ownNc)?.gravidade ?? 'moderada');
  const [link, setLink] = useState(ownNc ? '' : p?.ncId ?? '');
  const created = work.created.filter((n) => n.id !== ownNc);
  const linked = work.pending.find((n) => n.id === link);
  const pos = queue ? queue.indexOf(photoId) + 1 : 0;
  const next = queue && queue.length > 1 ? queue[pos % queue.length] : null;

  const save = useMutation({
    mutationFn: () => api.patch(`/api/t/${tid}/visits/${vid}/photos/${photoId}`, { setor, legenda: legenda.trim() || null, isNc: isNc && !link, ncId: link || null, gravidade: grav }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['visit', tid, vid] });
      if (next && next !== photoId && pos < (queue?.length ?? 0)) onNext(next);
      else onClose();
    },
    onError: (e) => toast(errMsg(e), 'err'),
  });

  if (!p) return null;
  const many = !!queue && queue.length > 1;

  return (
    <Sheet open onClose={onClose} title={many ? `Foto ${pos} de ${queue!.length}` : 'Foto'} subtitle={p.setor || 'Sem setor'} wide>
      <button
        type="button"
        onClick={() => onOpenFull(p)}
        aria-label="Ver foto inteira"
        style={{ display: 'block', width: '100%', aspectRatio: '4 / 3', maxHeight: '34vh', border: 0, padding: 0, borderRadius: 12, marginBottom: 14, background: `url("${p.fullUrl}") center / contain no-repeat, ${photoBg(p.thumbUrl, p.id)}`, backgroundColor: '#1d2223', cursor: 'zoom-in' }}
      />
      <form onSubmit={(e) => { e.preventDefault(); if (!setor) return toast('Escolha o setor', 'err'); save.mutate(); }}>
        <Field label="Observação da foto">
          <AutoTextarea minRows={3} placeholder={isNc ? 'Descreva a não conformidade (vira o título)' : 'O que esta foto mostra (sem limite de texto)'} value={legenda} onChange={(e) => setLegenda(e.target.value)} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 10, marginBottom: 10 }}>
          <label className="stack gap-6" style={{ cursor: 'default' }}>
            <span className="label">Setor</span>
            <SectorSelect tid={tid} sectors={sectors} value={setor} onChange={setSetor} allowEmpty={!p.setor} />
          </label>
          {!editing && (
            <label className="stack gap-6" style={{ cursor: 'default' }}>
              <span className="label">Vincular a pendência</span>
              <select
                className="select sm"
                value={link}
                onChange={(e) => {
                  const v = e.target.value;
                  setLink(v);
                  const n = [...work.pending, ...created].find((x) => x.id === v);
                  if (n) { setSetor(n.setor); setIsNc(false); }
                }}
              >
                <option value="">Nenhuma</option>
                {work.pending.length > 0 && <optgroup label="Pendências anteriores">{work.pending.map((n) => <option key={n.id} value={n.id}>{n.titulo}</option>)}</optgroup>}
                {created.length > 0 && <optgroup label="Abertas nesta visita">{created.map((n) => <option key={n.id} value={n.id}>{n.titulo}</option>)}</optgroup>}
              </select>
            </label>
          )}
        </div>
        {linked && (
          <div className="small" style={{ margin: '-2px 0 10px', color: linked.mark === 'resolvida' ? 'var(--resolved)' : 'var(--text-2)' }}>
            {linked.mark === 'resolvida' ? 'Pendência resolvida: esta foto entra como DEPOIS no relatório.' : 'Marque a pendência como resolvida para a foto entrar como DEPOIS.'}
          </div>
        )}
        {!editing ? (
          <>
            <button type="button" className={`check-row ${isNc ? 'on' : ''}`} style={{ marginBottom: isNc ? 8 : 16 }} aria-pressed={isNc} onClick={() => { setIsNc(!isNc); setLink(''); }}>
              <span className="box">{isNc ? '✓' : ''}</span>
              <span className="flex1" style={{ fontSize: 13.5, fontWeight: 500 }}>Nova não conformidade</span>
            </button>
            {isNc && (
              <div style={{ marginBottom: 16 }}>
                <div className="small" style={{ marginBottom: 6 }}>Gravidade</div>
                <GravidadePicker value={grav} onChange={setGrav} />
              </div>
            )}
          </>
        ) : ownNc ? (
          <div style={{ marginBottom: 16 }}>
            <div className="small" style={{ marginBottom: 6 }}>Gravidade da não conformidade</div>
            <GravidadePicker value={grav} onChange={setGrav} />
          </div>
        ) : null}
        <div className="row gap-8">
          {many && pos < queue!.length && <Button type="button" className="flex1" size="lg" onClick={() => onNext(next)}>Pular</Button>}
          <Button type="submit" variant="dark" size="lg" className="flex1" loading={save.isPending} disabled={!setor}>
            {many && pos < queue!.length ? 'Salvar e próxima' : 'Salvar'}
          </Button>
        </div>
        <button type="button" className="link-btn muted" style={{ display: 'block', margin: '14px auto 0', fontSize: 12.5 }} onClick={() => onDelete(p)}>Remover esta foto</button>
      </form>
    </Sheet>
  );
}

function LocalUpload({ u, ncTitle }: { u: UploadItem; ncTitle?: string }) {
  const failed = u.status === 'failed';
  const warn = u.status === 'auth' || (u.status === 'waiting' && u.attempts > 0);
  const label =
    failed ? 'falhou'
      : u.status === 'auth' ? 'aguardando login'
        : u.status === 'preparing' ? 'preparando'
          : u.status === 'confirming' ? 'confirmando'
            : u.status === 'uploading' ? 'enviando'
              : u.attempts > 0 ? 'sem conexão' : 'na fila';
  return (
    <div className="upl" style={failed ? { borderColor: 'var(--nc-border)', background: '#FFFBFA' } : warn ? { borderColor: 'var(--persist-border)' } : undefined}>
      <div className="thumb" style={{ background: u.previewUrl ? `url("${u.previewUrl}") center / cover` : 'var(--divider)' }} />
      <div className="flex1" style={{ minWidth: 0 }}>
        <div className="wrapline" style={{ fontSize: 13, fontWeight: 500 }}>{u.legenda || 'Sem observação'}</div>
        <div className="wrapline" style={{ fontSize: 11.5, color: failed ? 'var(--nc-text)' : warn ? 'var(--persist-text)' : 'var(--text-2)', marginTop: 2 }}>
          {failed || warn ? u.error ?? 'Tentando de novo…' : `${u.setor || 'Sem setor'} · ${fmtTime(u.capturedAt)}${u.isNc ? ' · nova NC' : ''}${ncTitle ? ` · ${ncTitle}` : ''}`}
        </div>
        <div className="bar"><div className={failed ? 'fail' : u.status === 'uploading' || u.status === 'confirming' || u.status === 'preparing' ? 'indet' : ''} style={{ width: failed ? '100%' : u.status === 'confirming' ? '96%' : u.status === 'waiting' || u.status === 'auth' ? '4%' : undefined }} /></div>
        {(failed || warn) && (
          <div className="row gap-12" style={{ marginTop: 7 }}>
            <button className="link-btn u" onClick={() => void uploads.retry(u.localId)}>Tentar agora</button>
            {failed && <button className="link-btn u muted" onClick={() => void uploads.discard(u.localId)}>Descartar</button>}
          </div>
        )}
      </div>
      <span className="mono none" style={{ fontSize: 10.5, color: failed ? 'var(--nc-text)' : 'var(--text-2)' }}>{label}</span>
    </div>
  );
}
